import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import Auth from "./components/auth.jsx";
import { ThemeProvider } from "./contexts/ThemeContext.jsx";
import { MailboxScopeProvider } from "./contexts/MailboxScopeContext.jsx";
import Login from "./pages/login.jsx";
import OAuthCallback from "./pages/oauthCallback.jsx";
import Onboarding from "./pages/onboarding.jsx";
import { ONBOARDING_FIRST_PATH } from "./config/onboardingRoutes";
import Leads, { LeadDetailOutlet } from "./pages/leads.jsx";
import Audit from "./pages/audit.jsx";
import InboxAuditModal from "./components/leads/InboxAuditModal.jsx";
import PastAuditsModal from "./components/audit/PastAuditsModal.jsx";
import History, { HistoryDetailOutlet } from "./pages/history.jsx";
import Analytics from "./pages/analytics.jsx";
import Billing from "./pages/billing.jsx";
import SettingsLayout from "./pages/settings/SettingsLayout.jsx";
import SettingsBrand from "./pages/settings/Brand.jsx";
import SettingsBrandNew from "./pages/settings/BrandNew.jsx";
import SettingsMailboxes from "./pages/settings/Mailboxes.jsx";
import SettingsReplies from "./pages/settings/Replies.jsx";
import SettingsPreferences from "./pages/settings/Preferences.jsx";

function LegacyDashboardUrlRedirect() {
  const loc = useLocation();
  const rest = loc.pathname.replace(/^\/dashboard\/?/, "");
  const pathname = rest ? `/leads/${rest.replace(/^\//, "")}` : "/audit/";
  const normalized = pathname.endsWith("/") ? pathname : `${pathname}/`;
  return <Navigate to={`${normalized}${loc.search}${loc.hash}`} replace />;
}

const App = (props) => (
  <ThemeProvider>
    <Router>
      <Auth {...props}>
        {(authProps) => (
          <MailboxScopeProvider enabled={authProps.authenticated}>
            <Routes>
            <Route
              path="/"
              element={
                authProps.authenticated ? (
                  <Navigate to="/audit/" replace />
                ) : (
                  <Navigate to="/login/" replace />
                )
              }
            />
            <Route path="/login/" element={<Login {...authProps} />} />
            <Route path="/auth/callback/" element={<OAuthCallback />} />
            <Route path="/onboarding/" element={<Navigate to={ONBOARDING_FIRST_PATH} replace />} />
            <Route
              path="/onboarding/:stepSlug/"
              element={
                authProps.authenticated ? <Onboarding {...authProps} /> : <Navigate to="/login/" replace />
              }
            />
            <Route
              path="/leads/"
              element={
                authProps.authenticated ? <Leads {...authProps} /> : <Navigate to="/login/" replace />
              }
            >
              <Route path=":leadHash/" element={<LeadDetailOutlet />} />
            </Route>
            <Route path="/leads/new-audit/" element={<Navigate to="/audit/new/" replace />} />
            <Route path="/audit/new-audit/" element={<Navigate to="/audit/new/" replace />} />
            <Route
              path="/audit/"
              element={authProps.authenticated ? <Audit {...authProps} /> : <Navigate to="/login/" replace />}
            >
              <Route path="new/:bulkTaskHash/" element={<InboxAuditModal />} />
              <Route path="new/" element={<InboxAuditModal />} />
              <Route path="past/" element={<PastAuditsModal />} />
            </Route>
            <Route path="/dashboard/*" element={<LegacyDashboardUrlRedirect />} />
            <Route
              path="/history/"
              element={authProps.authenticated ? <History {...authProps} /> : <Navigate to="/login/" replace />}
            >
              <Route path=":inboundHash/" element={<HistoryDetailOutlet />} />
            </Route>
            <Route
              path="/analytics/"
              element={
                authProps.authenticated ? <Analytics {...authProps} /> : <Navigate to="/login/" replace />
              }
            />
            <Route
              path="/settings/"
              element={
                authProps.authenticated ? (
                  <SettingsLayout currentUser={authProps.currentUser} />
                ) : (
                  <Navigate to="/login/" replace />
                )
              }
            >
              <Route index element={<Navigate to="brand/" replace />} />
              <Route path="brand/" element={<SettingsBrand />}>
                <Route path="new/" element={<SettingsBrandNew />} />
              </Route>
              <Route path="replies/" element={<SettingsReplies />} />
              <Route path="tone/" element={<Navigate to="/settings/replies/" replace />} />
              <Route path="mailboxes/" element={<SettingsMailboxes />} />
              <Route path="preferences/" element={<SettingsPreferences />} />
            </Route>
            <Route
              path="/billing/"
              element={authProps.authenticated ? <Billing {...authProps} /> : <Navigate to="/login/" replace />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </MailboxScopeProvider>
        )}
      </Auth>
    </Router>
  </ThemeProvider>
);

export default App;
